import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import React, { type FC, type JSX, useEffect } from 'react';
import { type ReactConfirmProps, confirmable, createConfirmation } from 'react-confirm';
import { Button } from '../Inputs/Buttons';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import './ConfirmDialog.scss';

interface ConfirmDialogProps {
    title?: string | JSX.Element,
    message: string | JSX.Element,
    confirmLabel?: string | JSX.Element,
    cancelLabel?: string | JSX.Element
}

const ConfirmDialogComponent: FC<ConfirmDialogProps & ReactConfirmProps> = (props) => {
    const { show, proceed, title, message, confirmLabel, cancelLabel } = props;

    const { t } = useTranslation('common');

    const handleCancel = () => {
        restoreHiddenModal();
        proceed(false as any);
    }

    const handleConfirm = () => {
        proceed(true as any);
    }

    // Dismissing via the close icon is distinct from explicitly choosing the cancel/secondary
    // action - it resolves `undefined` so callers can tell "the user made no choice" apart from
    // "the user chose not to confirm" and treat it as a full no-op rather than defaulting to cancel's behavior.
    const handleClose = () => {
        restoreHiddenModal();
        proceed(undefined as any);
    }

    const restoreHiddenModal = () => {
        const hiddenModal = document.querySelector('.MuiModal-root.hidden');
        if (hiddenModal) {
            hiddenModal.classList.remove('hidden');
        }
    }

    // we don't want to show a dialog over a modal window, so we hide the modal first
    useEffect(() => {
        const existingModal = document.querySelector('.MuiModal-root');
        if (existingModal) {
            existingModal.classList.add('hidden');
        }

        // restore hidden modal window when dialog unmounts
        return restoreHiddenModal;
    }, [])

    return (
        <Dialog open={show} className="confirm_dialog">
            <DialogContent>
                <div className="confirm_dialog_header">
                    <h2 className="confirm_dialog_title">{title ?? t('confirm')}</h2>
                    <Button aria-label={t('close')} variant="text" round className="confirm_dialog_close_button" onClick={handleClose}>
                        <CloseIcon/>
                    </Button>
                </div>
                <div className="confirm_dialog_content">
                    {message}
                </div>
                <div className="confirm_dialog_actions">
                    <Button variant='text' onClick={handleCancel} autoFocus>{cancelLabel ?? t('cancel')}</Button>
                    <Button variant='opaque' onClick={handleConfirm}>{confirmLabel ?? t('confirm')}</Button>
                </div>
            </DialogContent>
        </Dialog>
    )
}

export const ConfirmDialog = confirmable(ConfirmDialogComponent);
// Resolves `true` (confirm), `false` (cancel/secondary action), or `undefined` (dismissed via the close icon - no choice made).
export const confirm: (props: ConfirmDialogProps) => Promise<boolean | undefined> = createConfirmation(ConfirmDialog) as any;